import { Badge, Box, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import Container from '@mui/material/Container';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ScoreGrade, scoreColor, sentenceScoreAllSilence } from '../Score';
import { ISentenceScore, ScoreApi } from '../api/ScoreApi';
import background from '../asset/background-registration.png';
import { handleErrorMessage } from '../component/ErrorDialog';
import { isJwtLoggedIn } from '../util/session';
import AppBarContext from '../context/AppBarContext';
import { pageWhiteCardSx } from '../style/pageWhiteCardSx';
import { LS_PENDING_SIGNUP_REFERRER_SENTENCE_SCORE_ID, LS_PUBLIC_SCORE_IMPORT_ID } from '../referrerStorage';


export default function PublicSentenceScore() {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    // Use the useParams hook to access the ID from the URL
    const { id } = useParams();

    const [sentenceScore, setSentenceScore] = React.useState<ISentenceScore | null>(null);

    const [isLoggedIn, setLoggedIn] = React.useState(false);

    React.useEffect(() => {
        if (isJwtLoggedIn()) {
            setLoggedIn(true);
        } else {
            setLoggedIn(false);
        }
    }, [history, location, setErrorMessage])

    React.useEffect(() => {
        ScoreApi.getPublicScore(id)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to CourseApi.getAll() " + response.status)
            })
            .then(score => {
                setSentenceScore(score);
                const publicId = String(id);
                // Import flow (Custom Sentence) reads "s" and clears it; signup reads pending key only.
                localStorage.setItem(LS_PUBLIC_SCORE_IMPORT_ID, publicId);
                localStorage.setItem(LS_PENDING_SIGNUP_REFERRER_SENTENCE_SCORE_ID, publicId);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [id, history, location, setErrorMessage]);

    return (
        <Container
            sx={{
                my: 0,
                display: 'flex', // Set display to flex to use Flexbox
                flexDirection: 'column', // Stack children vertically
                justifyContent: 'center', // Center children vertically in the container
                alignItems: 'center', // Center children horizontally
                backgroundImage: `url(${background})`, // Ensure 'background' contains the path to your image
                backgroundSize: 'cover', // Cover the entire container
                backgroundPosition: 'center', // Center the background image        
                minHeight: 'calc(100vh - 82px)', // Set the container's minimum height to fill the entire viewport, minus menu
                width: '100%', // Adjusted width to fill the entire viewport horizontally
            }}
        >
            {sentenceScore && <>
                <Typography variant="h2" sx={{ textAlign: 'center', color: (theme) => theme.palette.primary.main }}>Congratulate {sentenceScore.first_name} on their hard work!</Typography>

                <Box sx={{ ...pageWhiteCardSx, my: 4, mx: 'auto', width: 'fit-content', maxWidth: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ overflowX: 'auto', p: 2 }}>
                    <Table aria-label="simple table" sx={{ minWidth: 650, border: 'none', margin: 'auto', width: 'auto' }}>
                        <TableBody>
                            <TableRow>
                                <TableCell component="th" scope="row" sx={{ py: 1 }}>
                                    <Typography variant="h4">Word</Typography>
                                </TableCell>
                                {sentenceScore.score.map((value, index) =>
                                    <TableCell align="center" sx={{ py: 1 }}>
                                        {sentenceScore.text[index]}
                                    </TableCell>
                                )}
                            </TableRow>

                            <TableRow>
                                <TableCell component="th" scope="row" sx={{ py: 1 }}>
                                    <Typography variant="h4">Correct Pronunciation</Typography>
                                </TableCell>
                                {sentenceScore.score.map((value, index) =>
                                    <TableCell align="center" sx={{
                                        py: 1,
                                        backgroundColor: `${scoreColor(ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                    }}>
                                        {sentenceScore.pinyin_list_transcript[index]}
                                    </TableCell>
                                )}
                            </TableRow>

                            <TableRow>
                                <TableCell component="th" scope="row" sx={{
                                    py: 1,
                                    // display: 'flex', // Use Flexbox for alignment
                                    verticalAlign: 'middle',
                                    alignItems: 'center', // Vertically center the content
                                    gap: '8px', // Optional: Add space between the text and the image
                                }}>
                                    <Typography variant="h4">Your Pronunciation</Typography>
                                </TableCell>
                                {sentenceScoreAllSilence(sentenceScore) ? (
                                    <TableCell>Sentence Incomplete</TableCell>
                                ) : (sentenceScore.score.map((value, index) =>
                                    <TableCell align="center" sx={{
                                        py: 1,
                                        backgroundColor: `${scoreColor(sentenceScore ? sentenceScore.score_grade[index] : ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                    }}>
                                        {sentenceScore && sentenceScore.pinyin_list_spoken[index]}
                                    </TableCell>
                                ))}
                            </TableRow>

                            <TableRow>
                                <TableCell component="th" scope="row" sx={{ py: 1 }}>
                                    <Typography variant="h4">Total Score
                                        {(sentenceScore != null && sentenceScoreAllSilence(sentenceScore) == false) &&
                                            <Badge badgeContent={Math.round(sentenceScore.score_mean)} color="secondary"
                                                sx={{
                                                    '.MuiBadge-badge': {
                                                        backgroundColor: `${scoreColor(sentenceScore ? sentenceScore.passing_grade : ScoreGrade.Good)}22`,
                                                        fontSize: '1rem', // <-- Adjust font size here
                                                        fontWeight: 'bold', // optional
                                                        height: 32, // optional: increase badge size
                                                        minWidth: 32,
                                                        borderRadius: '50%', // keeps it circular if you increase size     

                                                    }, ml: 4,
                                                }}>
                                                {/* Empty div as a placeholder */}
                                                <div></div>
                                            </Badge>
                                        }
                                    </Typography>
                                </TableCell>
                                {sentenceScore.score.map((value, index) =>
                                    <TableCell align="center" sx={{
                                        py: 1,
                                        backgroundColor: `${scoreColor(sentenceScore ? sentenceScore.score_grade[index] : ScoreGrade.Good)}22`  // 22 is 13.33% opacity
                                    }}>
                                        {sentenceScore && Math.round(sentenceScore.score[index])}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                </Box>

            </>
            }
            <Typography sx={{ textAlign: 'center' }}>
                {isLoggedIn ? <>
                    You can attempt this sentence by going to PRONUNCIATION above.
                </> : <>
                    To attempt this sentence, please login or register above.
                </>}
            </Typography>
        </Container >
    );
}
